import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import { type FC, useEffect, useState } from "react";
import { ButtonComplete } from "@/components/buttons/ButtonComplete.tsx";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { IconClock } from "@/icons/IconClock.tsx";
import { Row } from "@/components/common/row.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import { toastAndError } from "@/helpers/toast.ts";
import type { UnlockCallback } from "@/components/buttons/shared.ts";
import type { Chat } from "@/stores/useChatStore.ts";
import { Slider } from "@/components/inputs/Slider.tsx";
import { Column } from "@/components/common/column.ts";
import { putTutorSocrateRating } from "@/api/tutors.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import type { QuizSessionDetailsResponse } from "@/api/quiz.ts";
import type { FlowGeneratorSession } from "@/types/flow.ts";

const ChatFeedbackWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  background: ${t.chatFooterBackground};
`;

const Gap = "0px";

const Feedback = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Gap};
  padding: 16px;
  background: white;
  margin: 20px ${t.chatPaddingSides};
  border-radius: ${t.brandBorderRadius};
  min-width: 600px;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Gap};
  overflow-x: scroll;
  scrollbar-width: none;
  z-index: 1000;
`;

const ButtonRow = styled(Row)`
  display: flex;
  justify-self: flex-end;
  margin: 12px ${t.chatPaddingSides};
`;

const SliderFeedback = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 500px;
`;

const SliderRotate = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  rotate: 90deg;
  width: 10rem;
  height: 10rem;
  translate: 0.5rem;
  scale: -100%;
`;

const FeedbackText = styled.p`
  text-align: left;

  cursor: pointer;
  user-select: none;
  transition: all 150ms ease;
  translate: -8rem;

  &[data-selected="true"] {
    color: ${t.brandPastelBlue};
    scale: 1.05;
  }

  &[data-selected="false"] {
    color: ${t.brandNeutralGreyDark};
    scale: 1;
  }
`;

interface ChatFeedbackProps {
  interventionId?: number;
  closeFeedback: () => void;
  chat: Chat;
  flowQuizSession?: QuizSessionDetailsResponse;
}

export const ChatFeedback: FC<ChatFeedbackProps> = ({
  chat,
  flowQuizSession,
  interventionId,
  closeFeedback,
}) => {
  const { tutorId } = useUserStore();
  const { SetSessionFeedbackGiven } = useAssignedChatStore();
  const [socrateRating, setSocrateRating] = useState<number>(3); // 1-5
  // const [overallRating, setOverallRating] = useState<number>(2); // 0-4
  // const [wow, setWow] = useState<boolean>(false);
  // const [negativeFeedback, setNegativeFeedback] = useState<string>();
  // const [positiveFeedback, setPositiveFeedback] = useState<string>();

  // Note: Forcing type as not undefined because ChatFeedback only renders if it's valid
  const flowSession = chat.flowGeneratorSession as FlowGeneratorSession;

  const questionNo = flowSession.flowGeneratorSession.flowItemGroupInputs.filter(
    (data) =>
      data.flowItemGroup.includes("CheckInQuestion_") &&
      !data.flowItemGroup.includes("_InterventionRating"),
  ).length;

  const currentQuestion = flowQuizSession?.questions.find((q) => q.sequence === questionNo);

  useEffect(() => {
    if (!interventionId) {
      closeFeedback();
    }
  }, [closeFeedback, interventionId]);

  if (!interventionId) {
    return <></>;
  }

  const SubmitFeedback = (unlockCallback: UnlockCallback) => {
    putTutorSocrateRating(tutorId, interventionId, socrateRating, false, "engagement rating")
      .then(() => {
        SetSessionFeedbackGiven(chat.flowGeneratorSessionId);
        closeFeedback();
      })
      .catch((e) => toastAndError("Could not update feedback", e))
      .finally(() => {
        unlockCallback();
      });
  };

  const FinishSession = () => {
    closeFeedback();
  };

  const fillColor = () => {
    switch (socrateRating) {
      case 5:
      case 4:
        return t.brandAccentRed;
      case 3:
        return t.brandAccentYellow;
      case 2:
      case 1:
        return t.brandAccentGreen;
      default:
        return t.sliderTrackedColor;
    }
  };

  return (
    <ChatFeedbackWrapper>
      <Feedback>
        <Main>
          <h1>Give your feedback</h1>
          <br />

          <Row data-justify={"between"} data-width={"full"}>
            {currentQuestion && (
              <SliderFeedback>
                <p>Why did you finish the intervention?</p>
                <Row data-justify={"center"}>
                  <SliderRotate>
                    <Slider
                      id={"socratesRating"}
                      state={socrateRating}
                      min={1}
                      max={3}
                      onChange={(newNumber) => {
                        setSocrateRating(newNumber);
                      }}
                      fillColor={fillColor()}
                      trackColor={fillColor()}
                      fillFromMiddle={true}
                    />
                  </SliderRotate>
                  <Column data-justify={"between"} data-align={"start"}>
                    <FeedbackText
                      data-selected={socrateRating === 3}
                      onClick={() => setSocrateRating(3)}
                    >
                      Student disengaged
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 2}
                      onClick={() => setSocrateRating(2)}
                    >
                      Successful intervention
                    </FeedbackText>
                    <FeedbackText
                      data-selected={socrateRating === 1}
                      onClick={() => setSocrateRating(1)}
                    >
                      Other, not disengaged (e.g., lesson finished)
                    </FeedbackText>
                  </Column>
                </Row>
              </SliderFeedback>
            )}
          </Row>
        </Main>
      </Feedback>
      <ButtonRow>
        <ButtonIcon
          backgroundColor={"#292D66E5"}
          icon={<IconClock color={"white"} />}
          text={"I'll do it later"}
          onClick={(unlock) => {
            FinishSession();
            unlock();
          }}
          flare
        />
        <ButtonComplete
          text={"Submit feedback"}
          onClick={(unlockCallback) => SubmitFeedback(unlockCallback)}
          flare
        />
      </ButtonRow>
    </ChatFeedbackWrapper>
  );
};
